<template>
  <transition name="drawer" v-if="is.compareDrawerSelectorOpen">
    <section class="unum-drawer-compare-selector">
      <div class="unum-drawer-compare-selector__head">
        <div class="head__top">
          <span class="menu-close" @click="closeCompareDrawerSelectorEvent">Close Drawer</span>
          <h3>Add a Platform</h3>
          <p v-if="this.filteredPlatforms.length > 0">
            Based on your selections, these platforms align with your preferences. Select one to see its features.
          </p>
        </div>
        <form
          @submit.prevent="search"
          class="unum-drawer-compare-selector__search"
        >
          <unum-textbox
            name="lookupTerms"
            label="Search for a platform"
            placeholder="Search for a platform"
            v-model.trim="lookupTerms"
            hideLabel
            noMargin
          />
          <unum-button :color="'green'">Search</unum-button>
        </form>
      </div>
      <div class="unum-drawer-compare-selector__body">
        <div class="body__v-bar-content">
          <div class="body__content">
            <section class="results results--preferred">
              <header class="results__header"><h3>Preferred</h3></header>
              <transition-group
                name="results"
                tag="ul"
                class="results__list"
                v-bind:css="false"
                v-on:before-enter="resultsBeforeEnter"
                v-on:enter="resultsEnter"
                v-on:leave="resultsLeave"
                v-if="results.length"
              >
                <li
                  v-for="(platform, index) in preferredPlatformsSorted"
                  v-bind:key="platform['.key']"
                  v-bind:data-index="index"
                >
                  <p
                    :id="'key-' + platform['.key']"
                    :data-platform-key="platform['.key']"
                    @click="addPlatformEvent(platform); closeCompareDrawerSelector();"
                  >
                    {{platform.displayName}}
                    <span
                      v-if="is.dev"
                      class="platform-priority"
                    >
                      priority: {{ platformPriority(platform.priority) }}
                    </span>
                  </p>
                  <unum-button
                    small
                    outline
                    color="red"
                    v-if="isChosen(platform)"
                    @click.native.stop="removePlatform(platform); closeCompareDrawerSelector();"
                  >
                    Remove
                  </unum-button>
                </li>
              </transition-group>
            </section>

            <section class="results results--fully-integrated">
              <header class="results__header"><h3>Fully Integrated</h3></header>
              <transition-group
                name="results"
                tag="ul"
                class="results__list"
                v-bind:css="false"
                v-on:before-enter="resultsBeforeEnter"
                v-on:enter="resultsEnter"
                v-on:leave="resultsLeave"
                v-if="results.length"
              >
                <li
                  v-for="(platform, index) in fullyIntegratedPlatformsSorted"
                  v-bind:key="platform['.key']"
                  v-bind:data-index="index"
                >
                  <p
                    :id="'key-' + platform['.key']"
                    :data-platform-key="platform['.key']"
                    @click="addPlatformEvent(platform); closeCompareDrawerSelector();"
                  >
                    {{platform.displayName}}
                    <span
                      v-if="is.dev"
                      class="platform-priority"
                    >
                      priority: {{ platformPriority(platform.priority) }}
                    </span>
                  </p>
                  <unum-button
                    small
                    outline
                    color="red"
                    v-if="isChosen(platform)"
                    @click.native.stop="removePlatform(platform); closeCompareDrawerSelector();"
                  >
                    Remove
                  </unum-button>
                </li>
              </transition-group>
            </section>

            <section class="results results--future-integration">
              <header class="results__header"><h3>Future Integration</h3></header>
              <transition-group
                name="results"
                tag="ul"
                class="results__list"
                v-bind:css="false"
                v-on:before-enter="resultsBeforeEnter"
                v-on:enter="resultsEnter"
                v-on:leave="resultsLeave"
                v-if="results.length"
              >
                <li
                  v-for="(platform, index) in futureIntegrationPlatformsSorted"
                  v-bind:key="platform['.key']"
                  v-bind:data-index="index"
                >
                  <p
                    :id="'key-' + platform['.key']"
                    :data-platform-key="platform['.key']"
                    @click="addPlatformEvent(platform); closeCompareDrawerSelector();"
                  >
                    {{platform.displayName}}
                    <span
                      v-if="is.dev"
                      class="platform-priority"
                    >
                      priority: {{ platformPriority(platform.priority) }}
                    </span>
                  </p>
                  <unum-button
                    small
                    outline
                    color="red"
                    v-if="isChosen(platform)"
                    @click.native.stop="removePlatform(platform); closeCompareDrawerSelector();"
                  >
                    Remove
                  </unum-button>
                </li>
              </transition-group>
            </section>
          </div>
        </div>
      </div>
    </section>
  </transition>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import Velocity from 'velocity-animate'

import UnumButton from '@/components/ui/Button'
import UnumTextbox from '@/components/ui/Textbox'

import sessionEvent from '@/util/sessionEvent'

export default {
  name: 'UnumDrawerCompareSelector',
  components: {
    UnumButton,
    UnumTextbox
  },
  data () {
    return {
      lookupTerms: '',
      results: '',
      vFuseOptions: {
        keys: ['displayName'],
        threshold: 0.2
      }
    }
  },
  async beforeMount () {
    if (this.filteredPlatforms.length > 0) {
      console.log('FILTERED PLATFORMS')
      // this.setPlatforms(await this.filteredPlatforms)
      this.results = await this.filteredPlatforms
    } else {
      console.log('ALL PLATFORMS')
      // this.setPlatforms(await this.platforms)
      this.results = await this.platforms
    }
  },
  methods: {
    ...mapActions('platforms', [
      'setPlatforms',
      'addPlatform',
      'removePlatform'
    ]),
    ...mapActions('shared', [
      'closeCompareDrawerSelector'
    ]),
    ...mapActions('user', [
      'logout'
    ]),
    isChosen (platformCheck) {
      if (this.chosenPlatforms
        .find(platform => platform['displayName'] === platformCheck['displayName'])) return true
      return false
    },
    isCurrent (platform) {
      if (platform && this.currentPlatform) {
        if (platform['displayName'] === this.currentPlatform['displayName']) {
          return true
        }
      }
    },
    search () {
      const sessionKey = this.$storage.get('session_key')
      sessionEvent(sessionKey, 'submit_add_platform_drawer_search', { search_term: this.lookupTerms })
      if (this.filteredPlatforms.length > 0) {
        this.$search(this.lookupTerms, this.filteredPlatforms, this.vFuseOptions)
          .then(results => {
            if (!results.length && !this.lookupTerms) {
              this.results = this.filteredPlatforms
              return
            }
            this.results = results
          })
      } else {
        this.$search(this.lookupTerms, this.platforms, this.vFuseOptions)
          .then(results => {
            if (!results.length && !this.lookupTerms) {
              this.results = this.platforms
              return
            }
            this.results = results
          })
      }
    },
    // Velocity animation methods
    resultsBeforeEnter (el) {
      el.style.opacity = 0
      el.style.maxHeight = 0
    },
    resultsEnter (el, done) {
      var delay = el.dataset.index * 50
      setTimeout(() => {
        Velocity(
          el,
          { opacity: 1, maxHeight: 420 }
        )
      }, delay)
    },
    resultsLeave (el, done) {
      var delay = el.dataset.index * 50
      setTimeout(() => {
        Velocity(
          el,
          { opacity: 0, height: 0, padding: 0, border: 0 }
        )
      }, delay)
    },
    addPlatformEvent (platform) {
      const sessionKey = this.$storage.get('session_key')
      sessionEvent(sessionKey, 'clicked_add_platform_drawer_platform', {
        platform: platform.displayName
      })
      this.addPlatform(platform)
    },
    closeCompareDrawerSelectorEvent () {
      const sessionKey = this.$storage.get('session_key')
      sessionEvent(sessionKey, 'clicked_add_platform_drawer_close_button')
      this.closeCompareDrawerSelector()
    },
    platformPriority (priority) {
      if (typeof priority === 'string') {
        if (priority.length) {
          return parseInt(priority, 10)
        }
        return 0
      }
      return '0, not set'
    }
  },
  computed: {
    ...mapState('platforms', [
      'chosenPlatforms',
      'currentPlatform',
      'platforms'
    ]),
    ...mapState('shared', [
      'is'
    ]),
    ...mapState('wizard', [
      'filteredPlatforms'
    ]),
    preferredPlatformsSorted () {
      const preferredResults = this.results.filter(platform => {
        return !!platform.preferred
      }).sort(function (a, b) {
        return a.displayName.toLowerCase() > b.displayName.toLowerCase() ? 1
          : b.displayName.toLowerCase() > a.displayName.toLowerCase() ? -1
            : 0
      })
      return preferredResults.sort(function (a, b) {
        // Account for cases where the platform's priority property doesn't exist
        const aPriority = a.priority ? parseInt(a.priority, 10) : 0
        const bPriority = b.priority ? parseInt(b.priority, 10) : 0
        return aPriority > bPriority ? 1 : bPriority > aPriority ? -1 : 0
      })
    },
    fullyIntegratedPlatformsSorted () {
      const fullyIntegratedResults = this.results.filter(platform => {
        return !platform.preferred && platform.status === 'fully_integrated'
      }).sort(function (a, b) {
        return a.displayName.toLowerCase() > b.displayName.toLowerCase() ? 1
          : b.displayName.toLowerCase() > a.displayName.toLowerCase() ? -1
            : 0
      })
      return fullyIntegratedResults.sort(function (a, b) {
        // Account for cases where the platform's priority property doesn't exist
        const aPriority = a.priority ? parseInt(a.priority, 10) : 0
        const bPriority = b.priority ? parseInt(b.priority, 10) : 0
        return aPriority > bPriority ? 1 : bPriority > aPriority ? -1 : 0
      })
    },
    futureIntegrationPlatformsSorted () {
      const futureIntegrationResults = this.results.filter(platform => {
        return !platform.preferred && platform.status === 'future_integration'
      }).sort(function (a, b) {
        return a.displayName.toLowerCase() > b.displayName.toLowerCase() ? 1
          : b.displayName.toLowerCase() > a.displayName.toLowerCase() ? -1
            : 0
      })
      return futureIntegrationResults.sort(function (a, b) {
        // Account for cases where the platform's priority property doesn't exist
        const aPriority = a.priority ? parseInt(a.priority, 10) : 0
        const bPriority = b.priority ? parseInt(b.priority, 10) : 0
        return aPriority > bPriority ? 1 : bPriority > aPriority ? -1 : 0
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.unum-drawer-compare-selector {
  background: #fff;
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  right: 0;
  transition: transform .5s cubic-bezier(0.77,0.2,0.05,1.0);
  box-shadow: -2px 0px 10px 0 rgba(0,0,0,0.25);
  z-index: 10;

  @include bp($medium) {
    width: 50%;
  }

  &__head {
    height: calc(8rem + 4.688rem);
    box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.25);
    z-index: 1;
    position: relative;
  }

  &__body {
    height: calc(100% - 8rem - 4.688rem);
    overflow: auto;
  }

  &__search {
    height: 4.688rem;
    background: $lightBlue;
    padding: .75rem 2rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;

    .unum-field {
      max-width: calc(100% - 1rem);
      margin: 0 1rem 0 0;
    }
  }
}

.head {
  &__top {
    padding: 2rem;
    padding-right: 6rem;
    height: 8rem;
    background: $lightBlue;
    // display: flex;
    // align-items: flex-end;

    h3 {
      margin: 0;
      color: $blue;
      font-weight: 600;
    }

    p {
      font-size: .875rem;
    }
  }
}

.menu-close {
  position: absolute;
  top: 3.5rem;
  right: 3rem;
  text-indent: -9999px;
  background: url('~@/assets/images/ui/x-blue.svg') center center no-repeat;
  background-size: contain;
  width: 2rem;
  height: 2rem;
  cursor: pointer;
}

// animation
.drawer {
  &-enter-active,
  &-fade-leave-active {
    transform: translate3d(0, 0, 0);
  }

  &-enter,
  &-leave-to {
    transform: translate3d(100%, 0, 0);
  }
}

.platform-priority {
  color: #666;
  font-size: 0.5rem;
}
</style>



// WEBPACK FOOTER //
// src/components/layout/DrawerCompareSelector.vue